// P41 + P27 (Q2, Q4, Q9 — RELEASE-TENIS10 § 11): the Control Panel's "Add entry" on a Tenis10 competition enters a CHILD,
// as SportyaOS does: the child search (the child's name, the parent's name, email or phone — a child nobody has claimed yet
// too, through its tenis10.ro family, while the switch is open), the quote, who pays, the entry. A claimed child: an entry paid
// on site — the default, also with nothing to front (Tudor, 2026-10-27) — or the parent's wallet (when it covers the whole
// total); a child not claimed yet: only paid on site. The api is
// the SportyaOS one behind administrator routes (`/admin/tenis10/...`); the outcome comes on the socket, matched by the
// request's correlation id. P45: the options are whole choice cards, worded as SportyaOS words them since 23.10 („Înscriere
// la fața locului”: nothing fronted, no outstanding balance).
// P54 (Tudor, question 7, 2026-10-25): the search is for THIS competition — the claimed children first, then the unclaimed
// ones; a child the competition does not admit (or already holds) is listed greyed, with the reason, and cannot be selected;
// the hits it does not show are counted under the list.
import React, { useContext, useEffect, useRef, useState } from "react"
import SearchBox from "components/Common/SearchBox"
import { Badge, Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap"
import { get, post } from "helpers/api_helper"
import { store as socketStore } from "components/Common/Socket/store"
import { LEVELS, childNumber } from "pages/Tenis10Claims/claimsLabels"
import { createEventOperationId } from "../../../../../eventOperationId"
import { entryMessage } from "./useEntryOperation"
import "./tenis10ChildEntry.scss"

const MIN_SEARCH = 3
const SEARCH_DEBOUNCE_MS = 300
// No answer on the socket by then: the entry may still land — the lists are read again and the admin checks them.
const RESULT_TIMEOUT_MS = 30000

export const TENIS10_ENTRY_MESSAGES = {
  PROFILE_NOT_ELIGIBLE: "The child is not eligible for this competition (level, age category or gender).",
  PASSPORT_INACTIVE: "The child's Tenis10 Passport is inactive.",
  MANAGED_PROFILE_NOT_FOUND: "The child's profile was not found.",
  MANAGED_PROFILE_OWNER_UNAVAILABLE: "The child's account could not be read right now. Nothing was changed. Retry.",
  UNCLAIMED_PAYMENT_ON_SITE_ONLY: "A child not claimed yet is entered only paid on site.",
  UNCLAIMED_ENTRIES_CLOSED: "Entering children not claimed yet is not open.",
  UNCLAIMED_PROFILE_ALREADY_CLAIMED: "The child was just claimed by a parent. Search again.",
  UNCLAIMED_PROFILE_HIDDEN: "The child's profile was hidden at the family's request.",
  UNCLAIMED_PROFILE_GROWN_UP: "The player is 16 or older, not a Tenis10 child.",
  FAMILY_HOLDER_CHANGED: "The child's tenis10.ro family changed. Search again.",
  OVERRIDE_FEE_REQUIRED: "There is nothing to pay on site: choose the parent's wallet.",
  PAYMENT_CHOICE_REQUIRED: "Choose who pays.",
  ENTRY_QUOTE_UNAVAILABLE: "The amounts could not be read right now. Nothing was changed. Retry.",
  ERROR_BANNED_USER: "The parent's account is suspended.",
  PROGRAM_MISMATCH: "This is not a Tenis10 competition.",
}
// Regulament Tenis10 2.4 (Tudor, 2026-10-29): a child plays at most one weekend and one weekdays competition a week; the
// refusal's `data.playMode` is the kind the child already plays that week
const SAME_WEEK_KIND = { weekend: "a weekend competition", weekdays: "a weekday competition" }
const sameWeekText = playMode => `The child is already entered in ${SAME_WEEK_KIND[playMode] || "another competition of the same kind"} this week. `
  + "In a week (Monday to Sunday) a child plays at most one weekend competition and one weekday competition."
export const tenis10EntryText = (code, data) => (code === "PROFILE_REGISTERED_SAME_WEEK" ? sameWeekText(data?.playMode)
  : TENIS10_ENTRY_MESSAGES[code] || entryMessage(code))

const money = (amount, currency) => `${Number(amount) || 0} ${currency || "RON"}`
// os #189 (23.10): the club fronts nothing — the amount for Sportya is settled after the event, whatever the parent pays
const sportyaAmount = total => `The amount for Sportya (${total}) is paid after the event ends and does not depend on collecting from the parent.`
const entriesPath = (eventId, competitionId) => `/admin/tenis10/events/${eventId}/competitions/${competitionId}/entries`
const refusalCode = error => error?.response?.data?.message
const refusalData = error => error?.response?.data?.data

export const searchChildren = (string, { eventId, competitionId } = {}) =>
  get("/find-managed-profiles/search", { params: { string, eventId, competitionId } })
export const quoteChildEntry = ({ eventId, competitionId, profileId, slotType }) =>
  get(`${entriesPath(eventId, competitionId)}/quote`, { params: { profileId, slotType } })
export const enterChild = ({ eventId, competitionId, slotType, body, correlationId }) =>
  post(`${entriesPath(eventId, competitionId)}/${slotType}`, body, { headers: { "X-Correlation-Id": correlationId } })

const PASSPORT_COLOR = { active: "success", expiring: "warning", inactive: "danger" }
const passportLabel = hit => {
  if (hit.passportStatus === "inactive") return "Passport inactive"
  const year = hit.passportEndDate ? new Date(hit.passportEndDate).getUTCFullYear() : null
  return ["Passport", year, hit.passportStatus === "expiring" ? "expiring" : "active"].filter(Boolean).join(" ")
}

export const ParentContact = ({ parent = {}, unclaimed = false }) => {
  const name = [parent.firstName, parent.lastName].filter(Boolean).join(" ") || "—"
  return <div className="text-muted small" data-testid="child-parent">
    {unclaimed ? "tenis10.ro parent" : "Parent"}: {[name, parent.email, parent.phone].filter(Boolean).join(" · ")}
  </div>
}

// P73 (Tudor, 2026-10-26): where the child lives — its parent's Sportya account, or what tenis10.ro knew of a child not
// claimed yet: „Sector 1, Bucuresti”, the city alone when the county has its name (as the CP event reports write it, P71);
// null when neither is known.
const folded = value => String(value || "").normalize("NFD").replace(/[̀-ͯ]/g, "").trim().toLowerCase()
export const placeLabel = location => {
  const city = String(location?.city || "").trim()
  const county = String(location?.county || "").trim()
  if (!city || !county) return city || county || null
  return folded(city) === folded(county) ? city : `${city}, ${county}`
}

const ChildPlace = ({ place }) => <div className="text-muted small" data-testid="child-place">
  <i className="bx bx-map mr-1" aria-hidden="true" /><span className="sr-only">Location: </span>{place}
</div>

// The ages an age category admits, by 31 December of the competition's year (common/tenis10Eligibility)
const CATEGORY_AGES = { U8: "up to 8", U9: "up to 9", U10: "up to 10", U12: "up to 12", U14: "13–14" }
const levelName = level => LEVELS[level] || level || "—"
const GENDER = { female: "Girl", male: "Boy" }
const INELIGIBLE = {
  LEVEL_MISMATCH: ({ details = {} }) => `level ${levelName(details.profileLevel)} — this competition is ${levelName(details.competitionLevel)}.`,
  AGE_OUT_OF_CATEGORY: ({ details = {} }) =>
    `${details.ageByDec31} in the competition's year — ${details.ageCategory} is for ages ${CATEGORY_AGES[details.ageCategory] || "—"}.`,
  GENDER_MISMATCH: ({ details = {} }) => `this competition is for ${details.gender === "female" ? "girls" : "boys"} only.`,
  NO_LEVEL: () => "the child has no Tenis10 level.",
  NO_BIRTH_DATE: () => "the child's birth year is missing.",
  COMPETITION_DATA_INCOMPLETE: () => "the competition has no level, age category or start date set.",
}
const ENTERED = {
  mainList: "Already entered in this competition.",
  waitingList: "Already on this competition's waiting list.",
  preRegistrationList: "Already pre-registered in this competition.",
}
// Why the child cannot be added to this competition, or null: it is already in it, or the competition does not admit it
export const blockedReason = hit => {
  if (hit?.entered) return ENTERED[hit.entered] || ENTERED.mainList
  const verdict = hit?.eligibility
  if (!verdict || verdict.eligible !== false) return null
  return `Not eligible: ${(INELIGIBLE[verdict.reason] || (() => "the competition does not admit this child."))(verdict)}`
}

const ChildHit = ({ hit, onPick, disabled }) => {
  const blocked = blockedReason(hit)
  const place = placeLabel(hit.location)
  return <li className={`tenis10-child-hit d-flex align-items-center justify-content-between border rounded p-2 mb-2${blocked ? " is-blocked" : ""}`}
    data-testid="child-hit">
    <div className="mr-2">
      <div className="d-flex flex-wrap align-items-center">
        <strong className="mr-2">{hit.firstName} {hit.lastName}</strong>
        {/* RELEASE-TENIS10 § 11 P78: the child's own number right after its name */}
        {childNumber(hit) != null && <span className="text-muted mr-2">#{childNumber(hit)}</span>}
        {hit.age != null && <span className="text-muted mr-2">
          {[`${hit.age} yrs`, hit.ageCategory, GENDER[hit.gender]].filter(Boolean).join(" · ")}
        </span>}
        {hit.tenis10Level && <Badge color="light" className="mr-1">{levelName(hit.tenis10Level)}</Badge>}
        <Badge color={PASSPORT_COLOR[hit.passportStatus] || "light"} className="mr-1">{passportLabel(hit)}</Badge>
        {hit.unclaimed && <Badge color="warning">Not claimed yet</Badge>}
      </div>
      {place && <ChildPlace place={place} />}
      <ParentContact parent={hit.parent} unclaimed={hit.unclaimed === true} />
      {blocked && <div className="tenis10-child-hit-reason small" data-testid="child-blocked">{blocked}</div>}
    </div>
    {onPick && <Button size="sm" color="primary" disabled={disabled || Boolean(blocked)} aria-label={`Select ${hit.firstName} ${hit.lastName}`}
      onClick={() => onPick(hit)}>Select</Button>}
  </li>
}

// The hits the list does not show: counted (or, past the search's window, „more than”), never dropped silently
const moreText = ({ results, total, exact }) => {
  const more = total - results.length
  if (exact && more <= 0) return null
  return `Showing ${results.length} of ${exact ? "" : "more than "}${total} children. Refine the search — the child's full name, or the parent's email or phone — to see the others.`
}
const NO_SEARCH = { results: [], total: 0, exact: true, checked: true, loading: false, error: false }
// what the search really reads: the child's name; the parent's name, email or phone (a tenis10.ro family's email: the whole address)
const SEARCH_PLACEHOLDER = "A child's name, or the parent's name, email or phone — min. 3 characters"

// P45: the whole card is the label — the radio inside it, beside the title; the chosen card and a disabled one read at a glance
const Option = ({ value, selected, disabled, onSelect, title, chip, children }) => <Label check data-option={value}
  className={`tenis10-pay-option${selected ? " is-selected" : ""}${disabled ? " is-disabled" : ""}`}>
  <Input type="radio" name="tenis10-who-pays" value={value} checked={selected} disabled={disabled} onChange={() => onSelect(value)} />
  <div className="tenis10-pay-option-body">
    <div className="tenis10-pay-option-title"><strong>{title}</strong>{chip}</div>
    <ul>{children}</ul>
  </div>
</Label>

const Tenis10ChildEntryModal = ({ isOpen, onClose, onEntered, eventId, competitionId, slotType = "STANDARD", disabledReason }) => {
  const { state: { socket: { socket, socketClientId } = {} } = {} } = useContext(socketStore)
  const [query, setQuery] = useState("")
  const [search, setSearch] = useState(NO_SEARCH)
  const [picked, setPicked] = useState(null)
  const [quote, setQuote] = useState({ data: null, loading: false, error: null, stale: false })
  const [payment, setPayment] = useState(null)
  const [submit, setSubmit] = useState({ pending: false, error: null, notice: null })
  const pending = useRef(null)
  const searchSeq = useRef(0)
  // only the latest quote counts: a slower answer for a child left behind never sets the amounts or the payment
  const quoteSeq = useRef(0)
  const entered = useRef(onEntered)
  entered.current = onEntered

  const reset = () => {
    clearTimeout(pending.current?.timer)
    pending.current = null
    setQuery("")
    setSearch(NO_SEARCH)
    setPicked(null)
    setQuote({ data: null, loading: false, error: null, stale: false })
    setPayment(null)
    setSubmit({ pending: false, error: null, notice: null })
  }
  useEffect(() => { if (!isOpen) reset() }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  // the search, debounced; only the latest answer counts
  useEffect(() => {
    const string = query.trim()
    const seq = ++searchSeq.current
    if (picked) return undefined
    if (string.length < MIN_SEARCH) {
      setSearch(NO_SEARCH)
      return undefined
    }
    const timer = setTimeout(() => {
      setSearch(current => ({ ...current, loading: true, error: false }))
      searchChildren(string, { eventId, competitionId })
        .then(response => {
          if (seq !== searchSeq.current) return
          const results = response?.data?.results || []
          setSearch({
            results,
            total: Math.max(Number(response?.data?.totalResults) || 0, results.length),
            exact: response?.data?.totalExact !== false,
            // false: the competition could not be read — the quote still refuses a child it does not admit
            checked: response?.data?.eligibilityChecked !== false,
            loading: false,
            error: false,
          })
        })
        .catch(() => { if (seq === searchSeq.current) setSearch({ ...NO_SEARCH, error: true }) })
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [query, picked, eventId, competitionId])

  const readQuote = (hit, stale = false) => {
    const seq = ++quoteSeq.current
    setPayment(null)
    setQuote({ data: null, loading: true, error: null, stale })
    return quoteChildEntry({ eventId, competitionId, profileId: hit.profileId, slotType })
      .then(response => {
        if (seq !== quoteSeq.current) return
        const data = response?.data
        setQuote({ data, loading: false, error: null, stale })
        // paid on site for every child unless the administrator picks the parent's wallet (Tudor, 2026-10-27)
        setPayment("override")
      })
      .catch(error => {
        if (seq === quoteSeq.current) setQuote({ data: null, loading: false, error: tenis10EntryText(refusalCode(error) || "ENTRY_QUOTE_UNAVAILABLE", refusalData(error)), stale })
      })
  }
  const pick = hit => { setPicked(hit); setSubmit({ pending: false, error: null, notice: null }); readQuote(hit) }
  const back = () => { quoteSeq.current += 1; setPicked(null); setQuote({ data: null, loading: false, error: null, stale: false }); setPayment(null) }

  const finish = (notice, appearance = "success") => {
    clearTimeout(pending.current?.timer)
    pending.current = null
    setSubmit({ pending: false, error: null, notice })
    entered.current?.(notice, appearance)
  }

  // the outcome of the entry, on the socket: Events' refusal, or Payments' (and Events') confirmation — listened to only
  // while the modal is open
  useEffect(() => {
    if (!socket || !isOpen) return undefined
    const onResponse = payload => {
      if (!pending.current || payload?.correlationId !== pending.current.correlationId) return
      if (payload.success === true) return finish("The child was entered.")
      clearTimeout(pending.current.timer)
      pending.current = null
      if (payload.message === "ENTRY_PREVIEW_CHANGED" && picked) {
        setSubmit({ pending: false, error: null, notice: null })
        return readQuote(picked, true)
      }
      return setSubmit({ pending: false, error: tenis10EntryText(payload.message, payload.data), notice: null })
    }
    socket.on("events-response", onResponse)
    socket.on("payments-response", onResponse)
    return () => {
      socket.off("events-response", onResponse)
      socket.off("payments-response", onResponse)
    }
  }, [socket, isOpen, picked]) // eslint-disable-line react-hooks/exhaustive-deps

  const confirm = async () => {
    const data = quote.data
    if (!picked || !data || !payment || submit.pending) return
    const correlationId = createEventOperationId("tenis10-child-entry")
    pending.current = { correlationId, timer: setTimeout(() => finish("The result is not confirmed yet — check the entries list.", "warning"), RESULT_TIMEOUT_MS) }
    setSubmit({ pending: true, error: null, notice: null })
    try {
      await enterChild({
        eventId, competitionId, slotType, correlationId,
        body: {
          profileId: picked.profileId,
          payment,
          confirmedQuote: { fee: data.fee, passport: data.passport ?? null, total: data.total, currency: data.currency, covers: data.covers === true },
          socketClientId,
        },
      })
    } catch (error) {
      clearTimeout(pending.current?.timer)
      pending.current = null
      setSubmit({ pending: false, error: tenis10EntryText(refusalCode(error), refusalData(error)), notice: null })
    }
  }

  const data = quote.data
  const unclaimed = picked?.unclaimed === true || data?.unclaimed === true
  const total = data ? money(data.total, data.currency) : ""
  const parentName = [picked?.parent?.firstName, picked?.parent?.lastName].filter(Boolean).join(" ") || "the parent"
  const nothingToFront = data ? !(Number(data.total) > 0) : false

  // while an entry is on its way the modal stays: closing it would drop the answer
  const close = submit.pending ? undefined : onClose
  return <Modal isOpen={isOpen} toggle={close} size="lg">
    <ModalHeader toggle={close}>{slotType === "WILD_CARD" ? "Add wild card — Tenis10 child" : "Add entry — Tenis10 child"}</ModalHeader>
    <ModalBody>
      {disabledReason && <div className="alert alert-warning" role="alert">{disabledReason}</div>}
      {submit.notice && <div className="alert alert-success" role="status">{submit.notice}</div>}
      {!picked && <>
        <SearchBox autoFocus aria-label="Search a child" placeholder={SEARCH_PLACEHOLDER}
          value={query} maxLength={50} onValueChange={setQuery} />
        <div className="mt-2" aria-live="polite">
          {search.loading && <span className="text-muted"><Spinner size="sm" /> Searching…</span>}
          {search.error && <span className="text-danger" role="alert">The search could not run — try again.</span>}
          {!search.loading && !search.error && query.trim().length >= MIN_SEARCH && search.results.length === 0 && <span className="text-muted">No child found.</span>}
          {!search.loading && !search.checked && search.results.length > 0 && <span className="text-muted" data-testid="child-unchecked">
            The competition could not be read right now: each child is checked when selected.
          </span>}
        </div>
        <ul className="list-unstyled mt-2">{search.results.map(hit => <ChildHit key={hit.profileId} hit={hit} onPick={pick} disabled={Boolean(disabledReason)} />)}</ul>
        {!search.loading && moreText(search) && <p className="text-muted small mb-0" data-testid="child-more">{moreText(search)}</p>}
      </>}
      {picked && <>
        <ul className="list-unstyled"><ChildHit hit={picked} /></ul>
        {quote.loading && <p className="text-muted"><Spinner size="sm" /> Checking the amounts…</p>}
        {quote.error && <div className="alert alert-danger" role="alert">{quote.error}</div>}
        {quote.stale && <div className="alert alert-warning" role="alert">The amounts changed — nothing was taken. Choose again.</div>}
        {data && <>
          <dl className="d-flex flex-wrap mb-2" data-testid="child-quote">
            {data.passport && <><dt className="mr-1">Tenis10 Passport {data.passport.year}:</dt><dd className="mr-3">{money(data.passport.price, data.currency)}</dd></>}
            <dt className="mr-1">Sportya Service Fee:</dt><dd className="mr-3">{money(data.fee, data.currency)}</dd>
            <dt className="mr-1">Total:</dt><dd><strong>{total}</strong></dd>
          </dl>
          <p className="font-weight-bold mb-1">Who pays?</p>
          {unclaimed ? <Option value="override" selected={payment === "override"} onSelect={setPayment} title="Paid on site">
            {nothingToFront ? <li>Nothing to pay on site: the club fee is paid at the club.</li> : <>
              <li>The tenis10.ro family ({parentName}) pays the club <strong>{total}</strong> on site.</li>
              <li>{sportyaAmount(total)}</li>
            </>}
            {data.passport && <li>The passport becomes active right away, for the whole of {data.passport.year}.</li>}
            <li>The child is not claimed yet: nobody is notified now. The entry moves to the parent's account when they claim the child.</li>
          </Option> : <>
            <Option value="wallet" selected={payment === "wallet"} disabled={data.covers !== true} onSelect={setPayment} title="From the parent's wallet"
              chip={<Badge className={`badge-soft-${data.covers === true ? "success" : "danger"}`}>Balance {data.covers === true ? "covers" : "does not cover"} {total}</Badge>}>
              {data.covers === true ? <>
                <li>Sportya takes <strong>{total}</strong> from the wallet of {parentName}; the entry is paid.</li>
                {data.passport && <li>The passport becomes active right away, for the whole of {data.passport.year}.</li>}
              </> : <li>Sportya never takes partial amounts — add the child with payment on site or ask the parent to top up their wallet.</li>}
            </Option>
            <Option value="override" selected={payment === "override"} onSelect={setPayment} title="Entry paid on site">
              {nothingToFront ? <>
                <li>Nothing to pay on site: the club fee is paid at the club.</li>
                <li>The parent gets the usual entry confirmation by email and a notification.</li>
              </> : <>
                <li>{parentName} pays the club <strong>{total}</strong> on site.</li>
                <li>{sportyaAmount(total)}</li>
                {data.passport && <li>The passport becomes active right away, for the whole of {data.passport.year}.</li>}
                <li>The parent gets an email and a notification.</li>
              </>}
            </Option>
          </>}
        </>}
        {submit.error && <div className="alert alert-danger" role="alert">{submit.error}</div>}
      </>}
    </ModalBody>
    <ModalFooter>
      {picked && <Button color="secondary" outline disabled={submit.pending} onClick={back}>Back to search</Button>}
      <Button color="secondary" disabled={submit.pending} onClick={onClose}>Close</Button>
      {picked && data && <Button color="primary" disabled={!payment || submit.pending || Boolean(disabledReason)} onClick={confirm}>
        {submit.pending ? <><Spinner size="sm" /> Adding…</> : `${unclaimed ? "Enter, paid on site" : payment === "wallet" ? "Pay from wallet" : "Add with payment on site"} · ${total}`}
      </Button>}
    </ModalFooter>
  </Modal>
}

export default Tenis10ChildEntryModal
